/** FB#36 (CHRIS): the legacy convention was to render the PB window id (e.g.
 *  "w_productivity_config") as a subtitle under the page title for operator orientation.
 *  CHRIS reported -- correctly -- that this is meaningless to end users and just adds
 *  clutter. We now suppress any subtitle that's a bare PB-window identifier, or any
 *  subtitle whose only non-prose content is one. The pbWindow is still passed in the
 *  ScreenHelpButton popover for engineers debugging which PB source backs the screen.
 *
 *  <p>Patterns suppressed:
 *  <ul>
 *    <li>{@code "w_xxx"} -- bare window id</li>
 *    <li>{@code "w_xxx — anything"} or {@code "w_xxx -- anything"} (em-dash or double-hyphen)</li>
 *    <li>{@code "anything — w_xxx"} or {@code "anything -- w_xxx"}</li>
 *  </ul>
 *
 *  Subtitles that are purely descriptive (breadcrumbs, status strings, signed-in-as
 *  banners on the dashboard) are unaffected. */
export function cleanSubtitle(s: string | undefined): string | undefined {
  if (s == null) return undefined;
  const trimmed = s.trim();
  if (trimmed === '') return undefined;
  // Strip a leading "w_... [—-] " or trailing "[—-] w_..." segment.
  const cleaned = trimmed
    .replace(/^w_[a-z0-9_]+\s*(?:[—-]{1,2})\s*/i, '')
    .replace(/\s*(?:[—-]{1,2})\s*w_[a-z0-9_]+\s*$/i, '')
    .trim();
  // Bare w_... after stripping = the whole subtitle was the PB window id.
  if (/^w_[a-z0-9_]+$/i.test(cleaned)) return undefined;
  // John 26 Oct: no PB / source citations anywhere a user can see (PageLayout and Modal both
  // render subtitles through here). Many browse configs keep
  // the PB window in `subtitle` because TableBrowserPage derives the help pbWindow from it, so
  // the raw string stays and the display drops the cite here: parenthetical PB asides go,
  // "w_x (mode)" / "w_x › Tab" / "w_x · y" forms keep only trailing prose after " — ", and any
  // subtitle still carrying a PB/DB identifier is suppressed.
  const PB_TOKEN = /\b(?:w|dw|uo|ue|wf|cb|fsp|pop)_[a-z0-9_]+|\bdbxx\.|\bADR-\d|\bslice-\d|\bPB\b|\.sr[wd]\b/i;
  let shown = cleaned.replace(/\s*\(([^()]*)\)/g, (m, inner: string) =>
    PB_TOKEN.test(inner) ? '' : m,
  );
  if (/^w_[a-z0-9_]+/i.test(shown)) {
    const prose = /\s[—-]{1,2}\s+([^—]+)$/.exec(shown.replace(/\([^()]*\)/g, ''))?.[1]?.trim();
    shown = prose ?? '';
  }
  shown = shown.trim();
  if (shown === '' || PB_TOKEN.test(shown)) return undefined;
  return shown === cleaned ? shown : shown.charAt(0).toUpperCase() + shown.slice(1);
}
